import { useMemo, useState } from 'react';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { RenderUnsettledUI, SmallError, SmallLoader } from 'shared/components';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import {
  IconApproved,
  IconCall,
  IconCancel,
  IconCross,
  IconDocument,
  IconEdit,
  IconInbox,
} from 'assets/img';
import { LwButton, LwFormDate, LWModal, UserAvatar } from 'redesign';
import { DateTime } from 'luxon';
import { PrimaryButton, SecondaryButton, useDialog } from 'ogp/providers/dialog-provider';
import { accreditationsAlerts, useAlert } from 'shared/components/alerts';
import {
  ButtonBigWrap,
  ButtonWrap,
  ButtonWrapCertificate,
  CertificateInfo,
  CertificateInfoWrapper,
  CertificateName,
  CertificateWrap,
  CredentialText,
  DateBox,
  FlexColumnBoxWrap,
  FlexRowBoxCenter,
  HeaderWrapper,
  ImageWrap,
  InfoWrap,
  ModalWrapperBox,
  NameBox,
  OpenCertificateButton,
  StyledIcon,
} from 'shared/components/common/certificate-modal-styled-components';
import { useGetAccreditationDetail } from '../hooks/use-get-accreditation';
import { useUpdateAccreditation } from '../hooks/use-update-accreditation';
import { useApproveAccreditation } from '../hooks/use-approve-accreditation';
import { useRejectAccreditation } from '../hooks/use-reject-accreditation';
import { FlexWorkerDetail } from '../../flexWorkers/flex-worker-detail/flex-worker-detail';
import {
  AccreditationState,
  AccreditationType,
  RawAccreditationData,
} from '../../../../services/accreditations-service.types';

type AccreditationDetailProps = {
  flexWorkerId: string;
  certificateId: string;
  accreditationType: AccreditationType;
  close: () => void;
};

type CommonProps = {
  data: RawAccreditationData;
  closeModal: () => void;
};

export const AccreditationDetail = ({
  flexWorkerId,
  certificateId,
  accreditationType,
  close,
}: AccreditationDetailProps) => {
  const accreditationDetailQuery = useGetAccreditationDetail(
    certificateId,
    flexWorkerId,
    accreditationType
  );

  if (accreditationDetailQuery.status !== 'success') {
    return (
      <RenderUnsettledUI
        renderLoading={<SmallLoader />}
        renderError={(error) => <SmallError error={error} />}
        data={accreditationDetailQuery}
      />
    );
  }

  const { data } = accreditationDetailQuery;

  return (
    <ModalWrapperBox>
      <ProfileHeader data={data} close={close} />
      <ModalBody data={data} closeModal={close} />
    </ModalWrapperBox>
  );
};

const ProfileHeader = ({ data, close }: { data: RawAccreditationData; close: () => void }) => {
  const [open, setOpen] = useState(false);

  const toggleFwModal = () => {
    setOpen(!open);
  };

  return (
    <>
      <Box>
        <HeaderWrapper cursor="pointer">
          <FlexRowBoxCenter onClick={toggleFwModal} role="fw-profile">
            <ImageWrap>
              <UserAvatar
                firstName={data.flexWorker.firstName}
                lastName={data.flexWorker.lastName}
                imageUrl={data.flexWorker.imageUrl}
                width={14}
                height={14}
              />
            </ImageWrap>

            <Typography variant="h3" fontWeight={500} noWrap>
              {data?.flexWorker.firstName} {data?.flexWorker.lastName}
            </Typography>
          </FlexRowBoxCenter>

          <InfoWrap>
            <Box display="flex" flexDirection="row">
              <StyledIcon width={24} height={24} as={IconCall} />
              <CredentialText paddingRight={5} noWrap>
                {data?.flexWorker.phone}
              </CredentialText>
            </Box>
            <Box display="flex" flexDirection="row">
              <StyledIcon width={24} height={24} as={IconInbox} />
              <CredentialText noWrap>{data?.flexWorker.email}</CredentialText>
            </Box>
            <Box display="flex" flexDirection="row">
              <IconButton onClick={close}>
                <IconCross width={24} height={24} />
              </IconButton>
            </Box>
          </InfoWrap>
        </HeaderWrapper>
        <Divider />
      </Box>

      <LWModal isOpen={open} onClose={toggleFwModal} testId="detail-modal">
        {data.flexWorkerId ? (
          <FlexWorkerDetail id={data.flexWorkerId} onClose={toggleFwModal} />
        ) : null}
      </LWModal>
    </>
  );
};

const ModalBody = ({ data, closeModal }: CommonProps) => {
  const methods = useForm({
    defaultValues: {
      expirationDate: data.expirationDate
        ? DateTime.fromISO(data.expirationDate).toFormat('yyyy-MM-dd')
        : undefined,
    },
  });

  const handleOpenAccreditation = () => {
    if (data.fileUrl) {
      window.open(data.fileUrl, '_blank');
    }
  };

  return (
    <FormProvider {...methods}>
      <FlexColumnBoxWrap>
        <CertificateInfo>
          <CertificateWrap>
            <FlexRowBoxCenter>
              <IconDocument width={130} height={150} onClick={handleOpenAccreditation} />
            </FlexRowBoxCenter>
            <ButtonWrapCertificate>
              <OpenCertificateButton
                onClick={handleOpenAccreditation}
                role="button"
                disabled={!data.fileUrl}
              >
                Open
              </OpenCertificateButton>
            </ButtonWrapCertificate>
          </CertificateWrap>
          <CertificateInfoWrapper>
            <NameBox>
              <CertificateName>{`Certificaat: ${data?.certificate.name}`}</CertificateName>
            </NameBox>
            <NameBox>
              <CertificateName>{`Certificaattype: ${data.type}`}</CertificateName>
            </NameBox>
            <DateBox>
              <LwFormDate
                name="expirationDate"
                label="Vervaldatum instellen"
                control={methods.control}
              />
            </DateBox>
          </CertificateInfoWrapper>
        </CertificateInfo>
        <Buttons data={data} closeModal={closeModal} />
      </FlexColumnBoxWrap>
    </FormProvider>
  );
};

const Buttons = ({ data, closeModal }: CommonProps) => {
  const methods = useFormContext();
  const expirationDate = methods.watch('expirationDate');
  const expirationAsDateTime = expirationDate ? DateTime.fromISO(expirationDate) : null;
  const startDate = null; // For now we dont have start date in this view

  return (
    <ButtonBigWrap>
      <ButtonWrap>
        <ButtonsBasedOnAccreditationState
          accreditationState={data.state}
          flexWorkerId={data.flexWorkerId}
          accreditationType={data.type}
          certificateId={data.certificateId}
          expirationDate={expirationAsDateTime}
          startDate={startDate}
          closeModal={closeModal}
        />
      </ButtonWrap>
    </ButtonBigWrap>
  );
};

const ButtonsBasedOnAccreditationState = ({
  accreditationState,
  flexWorkerId,
  certificateId,
  accreditationType,
  expirationDate,
  startDate,
  closeModal,
}: {
  accreditationState: AccreditationState;
  flexWorkerId: RawAccreditationData['flexWorkerId'];
  certificateId: RawAccreditationData['certificateId'];
  accreditationType: RawAccreditationData['type'];
  expirationDate: DateTime | null;
  startDate: DateTime | null;
  closeModal: () => void;
}) => {
  switch (accreditationState) {
    case 'pending': {
      return (
        <>
          <RejectAccreditationButton
            flexWorkerId={flexWorkerId}
            certificateId={certificateId}
            accreditationType={accreditationType}
            closeModal={closeModal}
          />
          <ApproveAccreditationButton
            flexWorkerId={flexWorkerId}
            certificateId={certificateId}
            accreditationType={accreditationType}
            expirationDate={expirationDate}
            closeModal={closeModal}
          />
        </>
      );
    }
    case 'rejected': {
      return (
        <>
          <ApproveAccreditationButton
            flexWorkerId={flexWorkerId}
            certificateId={certificateId}
            accreditationType={accreditationType}
            expirationDate={expirationDate}
            closeModal={closeModal}
          />
          <UpdateAccreditationButton
            flexWorkerId={flexWorkerId}
            certificateId={certificateId}
            expirationDate={expirationDate}
            startDate={startDate}
            accreditationType={accreditationType}
            closeModal={closeModal}
          />
        </>
      );
    }
    case 'verified': {
      return (
        <>
          <RejectAccreditationButton
            flexWorkerId={flexWorkerId}
            accreditationType={accreditationType}
            certificateId={certificateId}
            closeModal={closeModal}
          />
          <UpdateAccreditationButton
            flexWorkerId={flexWorkerId}
            certificateId={certificateId}
            expirationDate={expirationDate}
            startDate={startDate}
            accreditationType={accreditationType}
            closeModal={closeModal}
          />
        </>
      );
    }
    case 'all': {
      return (
        <>
          <RejectAccreditationButton
            flexWorkerId={flexWorkerId}
            accreditationType={accreditationType}
            certificateId={certificateId}
            closeModal={closeModal}
          />
          <UpdateAccreditationButton
            flexWorkerId={flexWorkerId}
            certificateId={certificateId}
            expirationDate={expirationDate}
            startDate={startDate}
            accreditationType={accreditationType}
            closeModal={closeModal}
          />
        </>
      );
    }
  }
};

const UpdateAccreditationButton = ({
  flexWorkerId,
  certificateId,
  expirationDate,
  startDate,
  accreditationType,
  closeModal,
}: {
  flexWorkerId: RawAccreditationData['flexWorkerId'];
  certificateId: RawAccreditationData['certificateId'];
  expirationDate: DateTime | null;
  startDate: DateTime | null;
  accreditationType: RawAccreditationData['type'];
  closeModal: () => void;
}) => {
  const { openDialog, closeDialog, enableDialog, disableDialog } = useDialog();
  const { alertSuccess, alertError } = useAlert();
  const mutation = useUpdateAccreditation();

  const mutationData = useMemo(
    () => ({
      flexWorkerId,
      certificateId,
      expirationDate,
      startDate,
      accreditationType,
    }),
    [certificateId, flexWorkerId, expirationDate, startDate, accreditationType]
  );

  const handleUpdateAccreditation = () => {
    openDialog(
      {
        title: 'Weet u zeker dat u de vervaldatum van het certificaat wilt bijwerken?',
        secondaryButton: (props) => (
          <PrimaryButton iconColorMode="fill" startIcon={<IconCancel />} {...props}>
            Annuleer
          </PrimaryButton>
        ),
        primaryButton: (props) => (
          <SecondaryButton startIcon={<IconApproved />} {...props}>
            Bevestig
          </SecondaryButton>
        ),
      },
      () => {
        disableDialog();
        mutation.mutate(mutationData, {
          onSuccess: () => {
            closeDialog();
            closeModal();
            alertSuccess(accreditationsAlerts.success.updated);
          },
          onError: (e) => alertError(e),
          onSettled: () => {
            enableDialog();
          },
        });
      }
    );
  };

  return (
    <LwButton
      onClick={handleUpdateAccreditation}
      iconColorMode="stroke"
      color="primary"
      startIcon={<IconEdit />}
    >
      Bewerking
    </LwButton>
  );
};

const ApproveAccreditationButton = ({
  flexWorkerId,
  certificateId,
  expirationDate,
  accreditationType,
  closeModal,
}: {
  flexWorkerId: RawAccreditationData['flexWorkerId'];
  certificateId: RawAccreditationData['certificateId'];
  accreditationType: RawAccreditationData['type'];
  expirationDate: DateTime | null;
  closeModal: () => void;
}) => {
  const { openDialog, closeDialog, enableDialog, disableDialog } = useDialog();
  const { alertSuccess, alertError } = useAlert();
  const mutation = useApproveAccreditation();

  const mutationData = useMemo(
    () => ({
      flexWorkerId,
      certificateId,
      accreditationType,
      ...(expirationDate && { expirationDate }),
    }),
    [certificateId, flexWorkerId, expirationDate, accreditationType]
  );

  const handleApproveAccreditation = () => {
    openDialog(
      {
        title: 'Weet u zeker dat u het certificaat goedkeurt?',
        secondaryButton: (props) => (
          <SecondaryButton iconColorMode="fill" startIcon={<IconCancel />} {...props}>
            Annuleer
          </SecondaryButton>
        ),
        primaryButton: (props) => (
          <PrimaryButton startIcon={<IconApproved />} {...props}>
            Bevestig
          </PrimaryButton>
        ),
      },
      () => {
        disableDialog();
        mutation.mutate(mutationData, {
          onSuccess: () => {
            closeDialog();
            closeModal();
            alertSuccess(accreditationsAlerts.success.approved);
          },
          onError: (e) => alertError(e),
          onSettled: () => {
            enableDialog();
          },
        });
      }
    );
  };
  return (
    <LwButton
      onClick={handleApproveAccreditation}
      iconColorMode="stroke"
      color="primary"
      startIcon={<IconApproved />}
    >
      Goedkeuren
    </LwButton>
  );
};

const RejectAccreditationButton = ({
  flexWorkerId,
  certificateId,
  accreditationType,
  closeModal,
}: {
  flexWorkerId: RawAccreditationData['flexWorkerId'];
  certificateId: RawAccreditationData['certificateId'];
  accreditationType: RawAccreditationData['type'];
  closeModal: () => void;
}) => {
  const { openDialog, closeDialog, enableDialog, disableDialog } = useDialog();
  const { alertSuccess, alertError } = useAlert();
  const mutation = useRejectAccreditation();

  const mutationData = useMemo(
    () => ({
      flexWorkerId,
      certificateId,
      accreditationType,
    }),
    [certificateId, flexWorkerId, accreditationType]
  );

  const handleRejectAccreditation = () => {
    openDialog(
      {
        title:
          'Weet u zeker dat u het certificaat wilt afwijzen? Neem voor de details contact op met de flexwerker.',
        secondaryButton: (props) => (
          <PrimaryButton iconColorMode="fill" startIcon={<IconCancel />} {...props}>
            Annuleer
          </PrimaryButton>
        ),
        primaryButton: (props) => (
          <SecondaryButton startIcon={<IconApproved />} {...props}>
            Bevestig
          </SecondaryButton>
        ),
      },
      () => {
        disableDialog();
        mutation.mutate(mutationData, {
          onSuccess: () => {
            closeDialog();
            closeModal();
            alertSuccess(accreditationsAlerts.success.rejected);
          },
          onError: (e) => alertError(e),
          onSettled: () => {
            enableDialog();
          },
        });
      }
    );
  };
  return (
    <LwButton
      onClick={handleRejectAccreditation}
      iconColorMode="stroke"
      color="secondary"
      startIcon={<IconCancel />}
    >
      Afwijzen
    </LwButton>
  );
};
